import React, { useContext, useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  FiCheck,
  FiChevronLeft,
  FiExternalLink,
  FiFileText,
  FiInfo,
} from "react-icons/fi";
import Cancelimg from "../../assets/img/cancel-01.svg";
import LandLordContext from "../../context/landlord/LandLordContext";
import useTranslator from "../../hooks/useTranslator";
import {
  formatPropertyAddress,
  getUserDisplayName,
  toast,
} from "../../helper/common";
import LoadingSpinner from "../LoadingSpinner";
import "./InsuranceReviewAndPayPopup.css";

const formatAmount = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "-";

  return new Intl.NumberFormat("es-ES", {
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getTenants = (selectedTenants = []) =>
  selectedTenants
    .flatMap((selection) => {
      const participants = selection?.application_id?.participants;
      if (participants?.length) {
        return participants.map((participant) => participant?.tenant_info);
      }
      return [selection?.tenantid];
    })
    .filter(Boolean);

const InsuranceReviewAndPayPopup = ({
  show,
  onBack,
  onClose,
  property,
  currentLang,
  selectedTenants,
  selections,
  rent,
  insuranceCost,
  coverageMonths,
  preFilledInsuranceDocument,
  onPaymentLinkSent,
}) => {
  const { _t } = useTranslator();
  const { initiateInsuracePayment, sendInsuranceBeneficiaryPaymentLink } =
    useContext(LandLordContext);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [view, setView] = useState("review");
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [message, setMessage] = useState("");
  const [sendLoading, setSendLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [sentDetails, setSentDetails] = useState(null);

  const propertyName = formatPropertyAddress(property, currentLang);
  const tenants = useMemo(() => getTenants(selectedTenants), [selectedTenants]);
  const tenantNames = tenants
    .map((tenant) => getUserDisplayName(tenant))
    .filter(Boolean);
  const formattedCost = formatAmount(insuranceCost);
  const formattedRent = formatAmount(rent);
  const formattedIntroCost = String(currentLang || "en")
    .toLowerCase()
    .startsWith("es")
    ? `${formattedCost} €`
    : `€${formattedCost}`;
  const resolvedCoverageMonths = Number(coverageMonths) || 12;
  const isBusy = paymentLoading || sendLoading;

  useEffect(() => {
    if (show) return;
    setView("review");
    setRecipientName("");
    setRecipientEmail("");
    setMessage("");
    setFormErrors({});
    setSentDetails(null);
  }, [show]);

  const handlePayment = async () => {
    if (paymentLoading) return;

    setPaymentLoading(true);
    try {
      const response = await initiateInsuracePayment(property?._id, {
        report_rent: rent,
        selections,
      });

      if (response?.status === "success" && response?.paymentUrl) {
        window.location.assign(response.paymentUrl);
        return;
      }

      toast.error(_t("insuranceReviewPaymentPlatformError"));
    } catch (error) {
      toast.error(_t("insuranceReviewPaymentPlatformError"));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleSendPaymentLink = async () => {
    if (sendLoading) return;

    const nextErrors = {};
    const normalizedName = recipientName.trim();
    const normalizedEmail = recipientEmail.trim().toLowerCase();
    if (!normalizedName) {
      nextErrors.recipientName = _t("recipientNameRequired");
    } else if (normalizedName.length > 100) {
      nextErrors.recipientName = _t("recipientNameMaximum100Characters");
    }
    if (!normalizedEmail) {
      nextErrors.recipientEmail = _t("recipientEmailRequired");
    } else if (
      normalizedEmail.length > 254 ||
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)
    ) {
      nextErrors.recipientEmail = _t("enterValidEmail");
    }
    if (message.length > 500) {
      nextErrors.message = _t("messageMaximum500Characters");
    }
    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSendLoading(true);
    try {
      const response = await sendInsuranceBeneficiaryPaymentLink({
        property_id: property?._id,
        selections,
        recipient_name: normalizedName,
        recipient_email: normalizedEmail,
        message: message.trim(),
      });
      if (!response?.success) {
        throw new Error(response?.message || "Unable to send payment link");
      }

      setSentDetails(response.success_res);
      setView("sent");
      try {
        await onPaymentLinkSent?.();
      } catch (refreshError) {
        console.error(
          "Failed to refresh property after sending payment link",
          refreshError
        );
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          _t("errorSendingPaymentLink")
      );
    } finally {
      setSendLoading(false);
    }
  };

  const renderSendView = () => (
    <>
      <div className="irpm-top-row irpm-send-top-row">
        <button
          type="button"
          className="irpm-back-button"
          onClick={() => setView("review")}
          disabled={sendLoading}
        >
          <FiChevronLeft aria-hidden="true" />
          {_t("insuranceReviewBack")}
        </button>
        <button
          type="button"
          className="irpm-close-button irpm-send-close"
          onClick={onClose}
          disabled={sendLoading}
          aria-label={_t("close")}
        >
          <img src={Cancelimg} alt="" />
        </button>
      </div>

      <p className="irpm-eyebrow irpm-send-eyebrow">
        {_t("nonPaymentInsurance")}
      </p>
      <h2 className="irpm-title">{_t("insuranceReviewSendPaymentLink")}</h2>
      <p className="irpm-subtitle irpm-send-subtitle">
        {_t("insuranceSendPaymentIntroStart")} {formattedIntroCost}{" "}
        {_t("insuranceSendPaymentIntroEnd")}
      </p>

      <div className="irpm-summary irpm-send-summary">
        <div className="irpm-summary-row">
          <span>{_t("insuranceReviewPropertyLabel")}</span>
          <strong>{propertyName}</strong>
        </div>
        <div className="irpm-summary-row irpm-premium-row">
          <span>{_t("amount")}</span>
          <strong>
            <b>{formattedCost} €</b> / {_t("year")}, {_t("singlePayment")}
          </strong>
        </div>
      </div>

      <div className="irpm-send-form">
        <div className="irpm-send-field-group">
          <label
            className={`irpm-send-field ${formErrors.recipientName ? "irpm-send-field--error" : ""}`}
          >
            <span>
              {_t("recipientName")} <b>*</b>
            </span>
            <input
              type="text"
              value={recipientName}
              onChange={(event) => {
                setRecipientName(event.target.value);
                setFormErrors((current) => ({
                  ...current,
                  recipientName: null,
                }));
              }}
              placeholder={_t("enterRecipientName")}
              disabled={sendLoading}
              maxLength={100}
            />
          </label>
          {formErrors.recipientName && (
            <p className="error-message">{formErrors.recipientName}</p>
          )}
        </div>
        <div className="irpm-send-field-group">
          <label
            className={`irpm-send-field ${formErrors.recipientEmail ? "irpm-send-field--error" : ""}`}
          >
            <span>
              {_t("recipientEmail")} <b>*</b>
            </span>
            <input
              type="email"
              value={recipientEmail}
              onChange={(event) => {
                setRecipientEmail(event.target.value);
                setFormErrors((current) => ({
                  ...current,
                  recipientEmail: null,
                }));
              }}
              placeholder={_t("enterRecipientEmail")}
              disabled={sendLoading}
              maxLength={254}
            />
          </label>
          {formErrors.recipientEmail && (
            <p className="error-message">{formErrors.recipientEmail}</p>
          )}
        </div>
        <div className="irpm-send-field-group irpm-send-message-group">
          <label
            className={`irpm-send-field irpm-send-message ${formErrors.message ? "irpm-send-field--error" : ""}`}
          >
            <span>{_t("messageOptional")}</span>
            <textarea
              value={message}
              onChange={(event) => {
                setMessage(event.target.value);
                setFormErrors((current) => ({ ...current, message: null }));
              }}
              placeholder={_t("insurancePaymentLinkMessagePlaceholder")}
              disabled={sendLoading}
              rows={2}
              maxLength={500}
            />
            <div className="irpm-send-message-meta">
              <em>{_t("insurancePaymentLinkMessageHelp")}</em>
              <span>{message.length}/500</span>
            </div>
          </label>
          {formErrors.message && (
            <p className="error-message">{formErrors.message}</p>
          )}
        </div>
      </div>

      <div className="irpm-send-submit-row">
        <button
          type="button"
          className="irpm-pay-button irpm-send-submit"
          onClick={handleSendPaymentLink}
          disabled={sendLoading}
        >
          {sendLoading ? (
            <LoadingSpinner size="18px" color="#ffffff" borderWidth={2} />
          ) : null}
          {_t("insuranceReviewSendPaymentLink")}
        </button>
      </div>
    </>
  );

  const renderSentView = () => {
    const expiresAt = sentDetails?.expires_at
      ? new Date(sentDetails.expires_at)
      : null;
    const formattedExpiry = expiresAt
      ? new Intl.DateTimeFormat(currentLang || "en", {
          day: "2-digit",
          month: "short",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        }).format(expiresAt)
      : "-";

    return (
      <div className="irpm-sent-view">
        <button
          type="button"
          className="irpm-close-button irpm-sent-close"
          onClick={onClose}
          aria-label={_t("close")}
        >
          <img src={Cancelimg} alt="" />
        </button>
        <div className="irpm-sent-icon">
          <FiCheck aria-hidden="true" />
        </div>
        <h2 className="irpm-sent-title">
          {_t("paymentLinkSentTo")} {sentDetails?.recipient_email}
        </h2>
        <p className="irpm-sent-subtitle">
          {_t("insurancePaymentLinkSentExplanation")}
        </p>

        <div className="irpm-summary irpm-sent-summary">
          <div className="irpm-summary-row">
            <span>{_t("insuranceReviewPropertyLabel")}</span>
            <strong>{sentDetails?.property_name || propertyName}</strong>
          </div>
          <div className="irpm-summary-row">
            <span>{_t("amount")}</span>
            <strong>
              {formatAmount(sentDetails?.amount ?? insuranceCost)} €
            </strong>
          </div>
          <div className="irpm-summary-row">
            <span>{_t("validUntil")}</span>
            <strong>{formattedExpiry}</strong>
          </div>
        </div>

        <button
          type="button"
          className="irpm-pay-button irpm-done-button"
          onClick={onClose}
        >
          {_t("done")}
        </button>
      </div>
    );
  };

  return (
    <Modal
      show={show}
      onHide={() => {
        if (!isBusy) onClose();
      }}
      centered
      backdrop={isBusy ? "static" : true}
      keyboard={!isBusy}
      className="insurance-review-pay-modal"
    >
      <Modal.Body>
        {view === "send" ? renderSendView() : null}
        {view === "sent" ? renderSentView() : null}
        {view === "review" ? (
          <>
            <div className="irpm-top-row">
              <button
                type="button"
                className="irpm-back-button"
                onClick={onBack}
                disabled={paymentLoading}
              >
                <FiChevronLeft aria-hidden="true" />
                {_t("insuranceReviewBack")}
              </button>
              <button
                type="button"
                className="irpm-close-button"
                onClick={onClose}
                disabled={paymentLoading}
                aria-label={_t("close")}
              >
                <img src={Cancelimg} alt="" />
              </button>
            </div>

            <p className="irpm-eyebrow">{_t("insuranceReviewStepLabel")}</p>
            <h2 className="irpm-title">{_t("insuranceReviewTitle")}</h2>
            <p className="irpm-subtitle">{_t("insuranceReviewSubtitle")}</p>

            <div className="irpm-summary">
              <div className="irpm-summary-row">
                <span>{_t("insuranceReviewPropertyLabel")}</span>
                <strong>{propertyName}</strong>
              </div>
              <div className="irpm-summary-row irpm-summary-row--top">
                <span>{_t("insuranceReviewTenantsLabel")}</span>
                <strong>
                  {tenantNames.length
                    ? tenantNames.map((name, index) => (
                        <span key={`${name}-${index}`}>{name}</span>
                      ))
                    : "-"}
                </strong>
              </div>
              <div className="irpm-summary-row">
                <span>{_t("insuranceReviewRentCoveredLabel")}</span>
                <strong>
                  {formattedRent} € / {_t("month")}
                </strong>
              </div>
              <div className="irpm-summary-row">
                <span>{_t("insuranceReviewMonthsOfCoverLabel")}</span>
                <strong>
                  {resolvedCoverageMonths} {_t("months")}
                </strong>
              </div>
              <div className="irpm-summary-divider" />
              <div className="irpm-summary-row irpm-premium-row">
                <span>{_t("insuranceReviewAnnualPremiumLabel")}</span>
                <strong>
                  <b>{formattedCost} €</b> / {_t("year")}, {_t("singlePayment")}
                </strong>
              </div>
            </div>

            {preFilledInsuranceDocument && (
              <div className="irpm-policy-row">
                <a
                  href={preFilledInsuranceDocument}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="irpm-policy-link"
                >
                  <FiFileText aria-hidden="true" />
                  {_t("insuranceReviewViewPolicyConditions")}
                  <FiExternalLink aria-hidden="true" />
                </a>
              </div>
            )}

            <div className="irpm-note">
              <FiInfo aria-hidden="true" />
              <p>{_t("insuranceReviewPaymentTimingNote")}</p>
            </div>

            <div className="irpm-actions">
              <button
                type="button"
                className="irpm-send-link-button"
                onClick={() => setView("send")}
              >
                {_t("insuranceReviewSendPaymentLink")}
              </button>
              <button
                type="button"
                className="irpm-pay-button"
                onClick={handlePayment}
                disabled={paymentLoading}
              >
                {paymentLoading ? (
                  <LoadingSpinner size="18px" color="#ffffff" borderWidth={2} />
                ) : null}
                {_t("pay")} {formattedCost} € {_t("now")}
              </button>
            </div>
          </>
        ) : null}
      </Modal.Body>
    </Modal>
  );
};

export default InsuranceReviewAndPayPopup;
